// ============================================================================
// src/stores/PaymentStore.ts - PAYMENT MANAGEMENT STORE
// ============================================================================

import { makeAutoObservable, runInAction } from 'mobx';
import {
  Payment,
  PaymentStatus,
  PaymentFilters,
  PaymentStats,
  CreatePaymentData
} from '@/types/payments';
import { paymentServiceV2 } from '@/services/paymentService.v2';
import { RootStore } from '.';

/**
 * PaymentStore - Quản lý thanh toán
 * 
 * Features:
 * - Create và manage payments
 * - Payment verification
 * - Refund processing
 * - Statistics và analytics
 * - Integration với invoice generation
 */
export class PaymentStore {
  rootStore: RootStore;

  // ========================================
  // PAYMENT DATA
  // ========================================
  payments: Payment[] = [];
  selectedPayment: Payment | null = null;

  // ========================================
  // UI STATES
  // ========================================
  isLoading: boolean = false;
  isCreating: boolean = false;
  isProcessing: boolean = false;
  error: string | null = null;

  // ========================================
  // PAGINATION
  // ========================================
  currentPage: number = 1;
  pageSize: number = 20;
  totalPayments: number = 0;
  totalPages: number = 1;
  hasMore: boolean = true;

  // ========================================
  // FILTERS
  // ========================================
  filters: PaymentFilters = {};

  // ========================================
  // STATISTICS
  // ========================================
  stats: PaymentStats | null = null;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
  }

  // ========================================
  // INITIALIZATION
  // ========================================

  /**
   * Initialize store
   */
  async initialize(userId?: string) {
    await this.loadMyPayments(userId);
    await this.loadStats(userId);
  }

  // ========================================
  // LOAD PAYMENTS
  // ========================================

  /**
   * Load payments của user
   */
  async loadMyPayments(userId?: string, page: number = 1) {
    this.setLoading(true);
    this.setError(null);

    try {
      const result = await paymentServiceV2.getUserPayments(
        userId,
        this.filters,
        page,
        this.pageSize
      );

      runInAction(() => {
        if (page === 1) {
          this.payments = result.payments;
        } else {
          this.payments = [...this.payments, ...result.payments];
        }

        this.currentPage = result.pagination.page;
        this.totalPages = result.pagination.pageCount;
        this.totalPayments = result.pagination.total;
        this.hasMore = result.pagination.page < result.pagination.pageCount;
      });

      console.log('✅ Loaded payments:', result.payments.length);
    } catch (error: any) {
      this.setError(error.message || 'Không thể tải danh sách thanh toán');
      console.error('Load payments error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Load more payments
   */
  async loadMorePayments(userId?: string) {
    if (!this.hasMore || this.isLoading) {
      return;
    }

    await this.loadMyPayments(userId, this.currentPage + 1);
  }

  /**
   * Refresh payments
   */
  async refreshPayments(userId?: string) {
    await this.loadMyPayments(userId, 1);
  }

  // ========================================
  // PAYMENT DETAILS
  // ========================================

  /**
   * Get payment by documentId
   */
  async getPaymentById(documentId: string) {
    this.setLoading(true);
    this.setError(null);

    try {
      const payment = await paymentServiceV2.getPaymentById(documentId);

      runInAction(() => {
        this.selectedPayment = payment;
      });

      return payment;
    } catch (error: any) {
      this.setError(error.message || 'Không thể tải thông tin thanh toán');
      console.error('Get payment error:', error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // CREATE PAYMENT
  // ========================================

  /**
   * Create new payment
   */
  async createPayment(paymentData: CreatePaymentData) {
    this.setCreating(true);
    this.setError(null);

    try {
      const payment = await paymentServiceV2.createPayment(paymentData);

      runInAction(() => {
        this.payments.unshift(payment);
        this.totalPayments += 1;
        this.selectedPayment = payment;
      });

      console.log('✅ Payment created:', payment.paymentId);

      // Trigger invoice generation nếu payment completed
      if (payment.paymentStatus === PaymentStatus.COMPLETED) {
        await this.handlePaymentCompleted(payment);
      }

      return payment;
    } catch (error: any) {
      this.setError(error.message || 'Không thể tạo thanh toán');
      console.error('Create payment error:', error);
      throw error;
    } finally {
      this.setCreating(false);
    }
  }

  // ========================================
  // PAYMENT ACTIONS
  // ========================================

  /**
   * Complete payment (after gateway confirmation)
   */
  async completePayment(documentId: string, gatewayTransactionId: string) {
    this.setProcessing(true);
    this.setError(null);

    try {
      const payment = await paymentServiceV2.completePayment(
        documentId,
        gatewayTransactionId
      );

      runInAction(() => {
        // Update trong list
        const index = this.payments.findIndex(p => p.documentId === documentId);
        if (index !== -1) {
          this.payments[index] = payment;
        }

        // Update selected
        if (this.selectedPayment?.documentId === documentId) {
          this.selectedPayment = payment;
        }
      });

      console.log('✅ Payment completed:', payment.paymentId);

      // Trigger invoice generation
      await this.handlePaymentCompleted(payment);

      return payment;
    } catch (error: any) {
      this.setError(error.message || 'Không thể hoàn thành thanh toán');
      console.error('Complete payment error:', error);
      throw error;
    } finally {
      this.setProcessing(false);
    }
  }

  /**
   * Cancel payment
   */
  async cancelPayment(documentId: string, reason: string) {
    this.setProcessing(true);
    this.setError(null);

    try {
      const payment = await paymentServiceV2.cancelPayment(documentId, reason);

      runInAction(() => {
        // Update trong list
        const index = this.payments.findIndex(p => p.documentId === documentId);
        if (index !== -1) {
          this.payments[index] = payment;
        }

        // Update selected
        if (this.selectedPayment?.documentId === documentId) {
          this.selectedPayment = payment;
        }
      });

      console.log('✅ Payment cancelled');
      return payment;
    } catch (error: any) {
      this.setError(error.message || 'Không thể hủy thanh toán');
      console.error('Cancel payment error:', error);
      throw error;
    } finally {
      this.setProcessing(false);
    }
  }

  /**
   * Verify payment with gateway
   */
  async verifyPayment(paymentId: string, gatewayTransactionId: string) {
    this.setProcessing(true);
    this.setError(null);

    try {
      const result = await paymentServiceV2.verifyPayment(
        paymentId,
        gatewayTransactionId
      );

      console.log('✅ Payment verified:', result.verified);
      return result;
    } catch (error: any) {
      this.setError(error.message || 'Không thể xác minh thanh toán');
      console.error('Verify payment error:', error);
      throw error;
    } finally {
      this.setProcessing(false);
    }
  }

  /**
   * Process refund
   */
  async processRefund(
    documentId: string,
    amount: number,
    reason: string
  ) {
    this.setProcessing(true);
    this.setError(null);

    try {
      const payment = await paymentServiceV2.processRefund(
        documentId,
        amount,
        reason
      );

      runInAction(() => {
        // Update trong list
        const index = this.payments.findIndex(p => p.documentId === documentId);
        if (index !== -1) {
          this.payments[index] = payment;
        }

        // Update selected
        if (this.selectedPayment?.documentId === documentId) {
          this.selectedPayment = payment;
        }
      });

      console.log('✅ Refund processed');
      return payment;
    } catch (error: any) {
      this.setError(error.message || 'Không thể xử lý hoàn tiền');
      console.error('Process refund error:', error);
      throw error;
    } finally {
      this.setProcessing(false);
    }
  }

  // ========================================
  // PAYMENT COMPLETION HANDLER
  // ========================================

  /**
   * Handle payment completed - Generate invoice
   */
  private async handlePaymentCompleted(payment: Payment) {
    try {
      // Trigger invoice generation trong InvoiceStore
      if (this.rootStore.invoiceStore) {
        await this.rootStore.invoiceStore.generateInvoiceFromPayment(
          payment.documentId,
          {
            autoSendEmail: true,
            generatePDF: true,
            markAsIssued: true
          }
        );
      }

      console.log('✅ Invoice generation triggered');
    } catch (error) {
      console.error('❌ Failed to generate invoice:', error);
      // Don't throw - invoice generation failure shouldn't break payment flow
    }
  }

  // ========================================
  // FILTERS & SEARCH
  // ========================================

  /**
   * Update filters
   */
  updateFilters(newFilters: Partial<PaymentFilters>) {
    this.filters = {
      ...this.filters,
      ...newFilters
    };

    // Reload với filter mới
    this.loadMyPayments(undefined, 1);
  }

  /**
   * Clear all filters
   */
  clearFilters() {
    this.filters = {};
    this.loadMyPayments(undefined, 1);
  }

  /**
   * Remove một filter
   */
  removeFilter(filterKey: keyof PaymentFilters) {
    const newFilters = { ...this.filters };
    delete newFilters[filterKey];
    this.filters = newFilters;
    this.loadMyPayments(undefined, 1);
  }

  // ========================================
  // STATISTICS
  // ========================================

  /**
   * Load payment statistics
   */
  async loadStats(userId?: string) {
    try {
      if (!userId) {
        userId = this.rootStore.authStore.currentUser?.documentId;
      }

      if (!userId) {
        return;
      }

      const stats = await paymentServiceV2.getPaymentStats(userId);

      runInAction(() => {
        this.stats = stats;
      });
    } catch (error) {
      console.error('Load stats error:', error);
    }
  }

  // ========================================
  // COMPUTED PROPERTIES
  // ========================================

  /**
   * Get pending payments
   */
  get pendingPayments(): Payment[] {
    return this.payments.filter(
      p => p.paymentStatus === PaymentStatus.PENDING
    );
  }

  /**
   * Get completed payments
   */
  get completedPayments(): Payment[] {
    return this.payments.filter(
      p => p.paymentStatus === PaymentStatus.COMPLETED
    );
  }

  /**
   * Get failed payments
   */
  get failedPayments(): Payment[] {
    return this.payments.filter(
      p => p.paymentStatus === PaymentStatus.FAILED
    );
  }

  /**
   * Get recent payments (last 30 days)
   */
  get recentPayments(): Payment[] {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return this.payments.filter(
      p => new Date(p.createdAt) >= thirtyDaysAgo
    );
  }

  // ========================================
  // UTILITIES
  // ========================================

  /**
   * Format currency
   */
  formatCurrency(amount: number, currency: string = 'VND'): string {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency
    }).format(amount);
  }

  /**
   * Get payment status label
   */
  getStatusLabel(status: PaymentStatus): string {
    const labels: Record<PaymentStatus, string> = {
      [PaymentStatus.PENDING]: 'Chờ xử lý',
      [PaymentStatus.PROCESSING]: 'Đang xử lý',
      [PaymentStatus.COMPLETED]: 'Hoàn thành',
      [PaymentStatus.FAILED]: 'Thất bại',
      [PaymentStatus.REFUNDED]: 'Đã hoàn tiền',
      [PaymentStatus.PARTIALLY_REFUNDED]: 'Hoàn tiền một phần',
      [PaymentStatus.CANCELLED]: 'Đã hủy'
    };

    return labels[status] || status;
  }

  // ========================================
  // STATE SETTERS
  // ========================================

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setCreating(creating: boolean) {
    this.isCreating = creating;
  }

  setProcessing(processing: boolean) {
    this.isProcessing = processing;
  }

  setError(error: string | null) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  // ========================================
  // RESET
  // ========================================

  reset() {
    this.payments = [];
    this.selectedPayment = null;
    this.isLoading = false;
    this.isCreating = false;
    this.isProcessing = false;
    this.error = null;
    this.currentPage = 1;
    this.pageSize = 20;
    this.totalPayments = 0;
    this.totalPages = 1;
    this.hasMore = true;
    this.filters = {};
    this.stats = null;
  }
}

export default PaymentStore;
